import React, { useState, useEffect } from "react";
// import { useSelector } from "react-redux";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Table from "../../core/pagination/datatable";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import CommonFooter from "../../core/common/footer/commonFooter";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import {
  createUnit,
  getUnits,
  updateUnit,
  deleteUnit,
} from "../../api/unitApi"; // adjust path if needed
import { toast } from "react-toastify";
import { getCurrentUserPerms, hasWrite, isCurrentUserAdmin } from "../../utils/perm";

export const Units = () => {
  const user = useSelector((state) => state?.auth?.user);
  const userPerms = getCurrentUserPerms(user?.permissions || []);
  const canWriteUnits =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasWrite(userPerms, "units");

  // const dataSource = useSelector((state) => state.rootReducer.unit_data);

  const [unitName, setUnitName] = useState({ en: "", ar: "" });
  const [shortName, setShortName] = useState("");
  const [status, setStatus] = useState(true);
  const [units, setUnits] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editUnit, setEditUnit] = useState({
    id: "",
    unitName: { en: "", ar: "" },
    shortName: "",
    status: true,
  });
  // create unit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canWriteUnits) {
      toast.error(`❌ ${window.translate("You don't have permission to modify units")}`);
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    if (!unitName.en.trim() || !unitName.ar.trim() || !shortName.trim()) {
      toast.error(`${window.translate("All fields are required in both languages")}`);
      return;
    }

    try {
      await createUnit({ unitName, shortName, status });
      // toast.success("Unit created successfully!");
      toast.success(` ${window.translate("Unit created successfully!")}`);

      // Optionally reset form fields
      setUnitName({ en: "", ar: "" });
      setShortName("");
      setStatus(true);

      // Close modal programmatically
      document.getElementById("close-add-unit-btn")?.click();

      // Reload page after brief delay
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (error) {
          setSubmitting(false);
          if (error.response?.status === 409) {
            toast.error(`❌ ${window.translate("Unit with this English name already exists")}`);
          } else {
            toast.error(`❌ ${window.translate("Failed to create Unit")}`);
          }
        }
  };

  // get unit list
  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      const lang = localStorage.getItem("lang") || "en";
      const data = await getUnits();

      const formatted = data.map((unit, index) => ({
        key: index,
        id: unit._id || unit.id,
        unit: unit.unitName?.[lang] || "Unnamed",
        shortname: unit.shortName || "N/A",
        noofproducts: unit.productCount || 0,
        createdon: new Date(unit.createdAt).toLocaleDateString(),
        status: unit.status ? "Active" : "Inactive",
        originalData: unit,
      }));

      setUnits(formatted);
    } catch (err) {
      toast.error(`${window.translate("Failed to fetch units.")}`);
    }
  };

  // Update api
  const handleEditClick = (unitObj) => {
    setEditUnit({
      id: unitObj._id || unitObj.id,
      unitName: unitObj.unitName,
      shortName: unitObj.shortName,
      status: unitObj.status === true || unitObj.status === "true",
    });
  };

  const handleUpdateUnit = async () => {
    if (!canWriteUnits) {
      toast.error(`❌ ${window.translate("You don't have permission to modify units")}`);
      return;
    }
    const { id, unitName, shortName, status } = editUnit;

    if (!unitName.en.trim() || !unitName.ar.trim() || !shortName.trim()) {
      toast.error(`${window.translate("All fields are required in both languages")}`);
      return;
    }

    try {
      await updateUnit(id, { unitName, shortName, status });
      // toast.success("Unit updated successfully");
      toast.success(` ${window.translate("Unit updated successfully")}`);

      // Optional: Close the modal first
      document.getElementById("close-edit-unit-btn")?.click();

      // Delay then reload
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (error) {
      toast.error(`${window.translate("Failed to update unit")}`);
    }
  };

  useEffect(() => {
    setEditUnit((prev) => ({ ...prev }));
  }, [editUnit.status]);

  // const handleDeleteBrand = async () => {
  //   try {
  //     if (!selectedUnitId) return;
  //     const res = await deleteUnit(selectedUnitId);
  //     toast.success(res.message);
  //     fetchUnits(); // Refresh brand list after deletion
  //     // Optional: Close the modal first
  //     document.getElementById("close-edit-unit-btn")?.click();

  //     // Delay then reload
  //     setTimeout(() => {
  //       window.location.reload();
  //     }, 800);
  //   } catch (error) {
  //     toast.error(`${window.translate("Failed to delete brand")}`);
  //   }
  // };

  const handleDeleteBrand = async () => {
  const t = (s) => (window?.translate ? window.translate(s) : s);
  if (!canWriteUnits) {
    toast.error(`❌ ${t("You don't have permission to modify units")}`);
    return;
  }

  try {
    if (!selectedUnitId) return;

    await deleteUnit(selectedUnitId); // ignore server message

    toast.success(` ${t("Unit deleted successfully!")}`);

    // Refresh list & close modal (optional)
    fetchUnits();
    document.getElementById("close-edit-unit-btn")?.click();

    // Small delay so the toast is visible
    setTimeout(() => {
      window.location.reload();
    }, 800);
  } catch (error) {
    toast.error(`❌ ${t("Failed to delete unit")}`);
  }
};

  const columns = [
    {
      title: window.translate("Unit"),
      dataIndex: "unit",
      sorter: (a, b) => a.unit.length - b.unit.length,
    },
    {
      title: window.translate("Short Name"),
      dataIndex: "shortname",
      sorter: (a, b) => a.shortname.length - b.shortname.length,
    },

    {
      title: window.translate("No of Products"),
      dataIndex: "noofproducts",
      sorter: (a, b) => a.noofproducts.length - b.noofproducts.length,
    },
    {
      title: window.translate("Created Date"),
      dataIndex: "createdon",
      sorter: (a, b) => a.createdon.length - b.createdon.length,
    },
    {
      title: window.translate("Status"),
      dataIndex: "status",
      render: (text) => (
        <span
          className={`badge table-badge fw-medium fs-10 ${
            text.toLowerCase() === "inactive" ? "bg-danger" : "bg-success"
          }`}
        >
          {text}
        </span>
      ),
      sorter: (a, b) => a.status.length - b.status.length,
    },

    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) =>
        canWriteUnits ? (
          <div className="action-table-data">
            <div className="edit-delete-action">
              <Link
                className="me-2 p-2"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#edit-units"
                onClick={() => handleEditClick(record.originalData)}
              >
                <i data-feather="edit" className="feather-edit"></i>
              </Link>
              <Link
                data-bs-toggle="modal"
                data-bs-target="#delete-modal"
                className="p-2"
                to="#"
                onClick={() => setSelectedUnitId(record.id)}
              >
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            </div>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">{window.translate("Units")}</h4>
                <h6>{window.translate("Manage your units")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            {canWriteUnits && (
              <div className="page-btn">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add-units"
                >
                  <i className="ti ti-circle-plus me-2 ms-2"></i> {window.translate("Add Unit")}
                </Link>
              </div>
            )}
          </div>
          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("status")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("active")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        In{window.translate("active")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Shoe
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Electronics
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("sortLast7Days")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("recentlyAdded")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("ascending")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("descending")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("lastMonth")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("last7Days")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                {units.length > 0 ? (
                  <Table columns={columns} dataSource={units} />
                ) : (
                  <div className="text-center py-4 fw-medium text-muted">
                    {window.translate("No units found.")}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* /product list */}
        </div>
        <CommonFooter />
      </div>

      {/* Add Unit */}
      <div className="modal fade" id="add-units">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("addUnit")}</h4>
                  </div>
                  <button
                    id="close-add-unit-btn"
                    type="button"
                    className="close bg-danger text-white fs-16"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Unit")} (English)
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={unitName.en}
                        onChange={(e) =>
                          setUnitName({ ...unitName, en: e.target.value })
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Unit")} (Arabic)<span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={unitName.ar}
                        onChange={(e) =>
                          setUnitName({ ...unitName, ar: e.target.value })
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Short Name")}<span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={shortName}
                        onChange={(e) => setShortName(e.target.value)}
                      />
                    </div>
                    <div className="mb-0">
                      <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                        <span className="status-label">
                          {window.translate("status")}
                        </span>
                        <input
                          type="checkbox"
                          id="unit-status"
                          className="check"
                          checked={status}
                          onChange={() => setStatus((prev) => !prev)}
                        />
                        <label htmlFor="unit-status" className="checktoggle" />
                      </div>
                    </div>
                    <div className="modal-footer mt-3">
                      <button
                        type="button"
                        className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                        data-bs-dismiss="modal"
                      >
                        {window.translate("cancel")}
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary fs-13 fw-medium p-2 px-3"
                      >
                        {/* {window.translate("Add Unit")} */}
                        {submitting ? window.translate("processing") : window.translate("Add Unit")}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Add Unit */}
      {/* Edit Unit */}
      <div className="modal fade" id="edit-units">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("Edit Unit")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close bg-danger text-white fs-16"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body">
                  <form>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Unit")} EN<span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editUnit.unitName.en}
                        onChange={(e) =>
                          setEditUnit((prev) => ({
                            ...prev,
                            unitName: { ...prev.unitName, en: e.target.value },
                          }))
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Unit")} AR<span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editUnit.unitName?.ar || ""} 
                        onChange={(e) =>
                          setEditUnit((prev) => ({
                            ...prev,
                            unitName: { ...prev.unitName, ar: e.target.value },
                          }))
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Short Name")}<span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editUnit.shortName}
                        onChange={(e) =>
                          setEditUnit((prev) => ({
                            ...prev,
                            shortName: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="mb-0">
                      <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                        <span className="status-label">
                          {window.translate("status")}
                        </span>
                        <input
                          type="checkbox"
                          id="edit-unit-status"
                          className="check"
                          checked={editUnit.status}
                          onChange={() =>
                            setEditUnit((prev) => ({
                              ...prev,
                              status: !prev.status,
                            }))
                          }
                        />
                        <label
                          htmlFor="edit-unit-status"
                          className="checktoggle"
                        />
                      </div>
                    </div>
                  </form>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("cancel")}
                  </button>
                  <button
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    onClick={handleUpdateUnit}
                    data-bs-dismiss="modal"
                  >
                    
                    {window.translate("Save Changes")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Edit Unit */}
      <CommonDeleteModal onConfirm={handleDeleteBrand} />
    </>
  );
};
